import { Navigate, useNavigate, useParams } from "react-router-dom";
import { CourseCompletionQuizRun } from "@/components/courseCompletionQuiz/CourseCompletionQuizRun";
import { ErrorState } from "@/components/common/ErrorState";
import { LoadingState } from "@/components/common/LoadingState";
import { useCourseCompletionQuizRun } from "@/hooks/courseCompletionQuiz/useCourseCompletionQuizRun";
import { useCourseTitleInHeader } from "@/hooks/courseCompletionQuiz/useCourseTitleInHeader";
import { useEnrollment } from "@/hooks/useEnrollment";
import {
  courseCompletionQuizPath,
  needsAnswer,
} from "@/lib/courseCompletionQuiz";
import { QuizValidationService } from "@/services/QuizValidationService";

/** Enrollment problems win: the attempt cap lives on the enrollment, not the run. */
const messageWhenQuizFailedToLoad = (
  enrollmentError: { message?: string } | undefined,
  enrollmentLoaded: boolean,
  runError: { message?: string } | undefined
): string => {
  if (!enrollmentLoaded || enrollmentError) {
    return enrollmentError?.message ?? "Einschreibung nicht gefunden";
  }
  return runError?.message ?? "Einschreibung nicht gefunden";
};

/** The open run of the course completion quiz, answered one question at a time. */
export const CourseCompletionQuizRunPage = () => {
  const navigate = useNavigate();
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const {
    data: enrollment,
    isLoading: enrollmentLoading,
    error: enrollmentError,
  } = useEnrollment(enrollmentId);
  useCourseTitleInHeader(enrollment?.courseTitle);
  const {
    run,
    isLoading: runLoading,
    error: runError,
  } = useCourseCompletionQuizRun(enrollmentId);

  if (!enrollmentId) {
    return (
      <ErrorState
        title="Kursabschlussquiz konnte nicht geladen werden"
        message="Keine Einschreibungs-ID in der URL"
      />
    );
  }
  if (enrollmentLoading || runLoading) {
    return <LoadingState message="Quiz wird geladen..." />;
  }
  if (enrollmentError || !enrollment || runError) {
    return (
      <ErrorState
        title="Kursabschlussquiz konnte nicht geladen werden"
        message={messageWhenQuizFailedToLoad(
          enrollmentError,
          !!enrollment,
          runError
        )}
      />
    );
  }

  const quiz = enrollment.courseCompletionQuiz;
  const dashboardPath = `/course/${enrollmentId}/dashboard`;
  if (!quiz) {
    return <Navigate to={dashboardPath} replace />;
  }

  // No open run: the quiz has not been started, or it is over.
  if (!run && quiz.status === "in_progress") {
    return <Navigate replace to={dashboardPath} />;
  }
  if (!run) {
    return (
      <Navigate
        replace
        to={courseCompletionQuizPath(enrollmentId, quiz.status)}
      />
    );
  }
  // Every loaded question is already stored: the run is over for this learner.
  if (!run.questions.some(needsAnswer)) {
    return (
      <Navigate
        replace
        to={courseCompletionQuizPath(enrollmentId, "waiting")}
      />
    );
  }

  return (
    <CourseCompletionQuizRun
      run={run}
      maxAttempts={quiz.maxAttempts}
      questions={run.questions}
      onSubmit={(question, type, userInput) =>
        QuizValidationService.submitCourseCompletionQuizAnswer({
          enrollmentId,
          type,
          userInput,
          contentElementId: question.contentElementId,
          entryIndex: question.entryIndex,
        })
      }
      onFinish={() =>
        navigate(courseCompletionQuizPath(enrollmentId, "waiting"))
      }
    />
  );
};
